import React, { useCallback, useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { useToast } from '../../hooks/useToast';

const ENTITY_OPTIONS = [
  { value: '', label: 'All types' },
  { value: 'timesheet', label: 'Timesheet' },
  { value: 'op_code', label: 'Op code' },
  { value: 'extra', label: 'Extra' },
  { value: 'crew', label: 'Crew' },
  { value: 'crew_member', label: 'Crew member' },
  { value: 'employee', label: 'Employee' },
  { value: 'payroll_period', label: 'Payroll period' },
];

const ACTION_OPTIONS = [
  { value: '', label: 'All actions' },
  { value: 'create', label: 'Create' },
  { value: 'update', label: 'Update' },
  { value: 'soft_delete', label: 'Remove' },
  { value: 'restore', label: 'Restore' },
  { value: 'toggle_active', label: 'Active toggle' },
  { value: 'lock', label: 'Lock' },
  { value: 'unlock', label: 'Unlock' },
  { value: 'sync', label: 'Change Log sync' },
];

const RESTORABLE = new Set(['timesheet', 'op_code', 'extra', 'crew', 'crew_member', 'payroll_period']);

function fmtWhen(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString();
}

function snapshotText(json) {
  if (!json) return '';
  const obj = typeof json === 'string' ? (() => { try { return JSON.parse(json); } catch { return null; } })() : json;
  if (!obj) return '';
  return Object.entries(obj)
    .filter(([, v]) => v !== null && v !== undefined && v !== '')
    .map(([k, v]) => `${k}: ${v}`)
    .join(' · ');
}

const TimeTrackerAuditLog = ({ onBack }) => {
  const { instance, accounts } = useMsal();
  const { showToast } = useToast();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [entityType, setEntityType] = useState('');
  const [action, setAction] = useState('');
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const fetchRows = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ limit: '300', offset: '0' });
      if (entityType) params.set('entityType', entityType);
      if (action) params.set('action', action);
      if (search.trim()) params.set('search', search.trim());
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      const response = await authenticatedApiCall(
        instance, accounts, 'GET', `/time-tracker-audit?${params.toString()}`
      );
      if (response.data.success) {
        setRows(response.data.data || []);
        setTotal(response.data.total || 0);
      } else {
        throw new Error(response.data.message || 'Failed to load audit');
      }
    } catch (err) {
      showToast(err.response?.data?.message || err.message || 'Failed to load audit', 'error');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, entityType, action, search, from, to, showToast]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const handleRestore = async (row) => {
    if (!RESTORABLE.has(row.entity_type) || !row.entity_id) return;
    if (!window.confirm(`Restore this ${row.entity_type} (${row.entity_id})?`)) return;
    try {
      const response = await authenticatedApiCall(
        instance, accounts, 'POST', '/time-tracker-audit/restore',
        { entityType: row.entity_type, entityId: row.entity_id }
      );
      if (response.data.success) {
        showToast('Restored', 'success');
        fetchRows();
      } else {
        throw new Error(response.data.message || 'Restore failed');
      }
    } catch (err) {
      showToast(err.response?.data?.message || err.message || 'Restore failed', 'error');
    }
  };

  const handleExport = () => {
    const header = 'occurred_at,actor_email,actor_name,action,entity_type,entity_id,job_id,discipline_code,summary';
    const lines = rows.map((r) => [
      r.occurred_at, r.actor_email, r.actor_name, r.action, r.entity_type,
      r.entity_id, r.job_id, r.discipline_code, (r.summary || '').replace(/"/g, '""'),
    ].map((v) => `"${v == null ? '' : v}"`).join(','));
    const blob = new Blob([[header, ...lines].join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'time-tracker-audit.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 480 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '1rem 1.5rem', borderBottom: '1px solid #e5e7eb', backgroundColor: '#f9fafb',
      }}>
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6366f1', fontWeight: 600 }}
          >
            ← Back
          </button>
        )}
        <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>Audit log</h2>
        <span style={{ fontSize: 12, color: '#64748b' }}>Accounting and superadmin only · {total} events</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button
            type="button"
            onClick={handleExport}
            style={{
              padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6,
              background: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600,
            }}
          >
            Export CSV
          </button>
        </div>
      </div>

      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 8, padding: '12px 1.5rem',
        borderBottom: '1px solid #e5e7eb',
      }}>
        <select value={entityType} onChange={(e) => setEntityType(e.target.value)} style={filterStyle}>
          {ENTITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select value={action} onChange={(e) => setAction(e.target.value)} style={filterStyle}>
          {ACTION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={filterStyle} />
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={filterStyle} />
        <input
          type="search"
          placeholder="Search name, email, code…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ ...filterStyle, minWidth: 200 }}
        />
      </div>

      <div style={{ flex: 1, overflow: 'auto' }}>
        {loading ? (
          <div style={{ padding: 24, color: '#64748b' }}>Loading audit…</div>
        ) : rows.length === 0 ? (
          <div style={{ padding: 24, color: '#64748b' }}>No audit rows for these filters.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                {['When', 'Who', 'Action', 'What', 'Summary', ''].map((h) => (
                  <th key={h} style={{ padding: '8px 10px', borderBottom: '1px solid #e5e7eb', position: 'sticky', top: 0, background: '#f8fafc' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} style={{ borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' }}>
                  <td style={{ padding: '8px 10px', whiteSpace: 'nowrap', color: '#334155' }}>{fmtWhen(row.occurred_at)}</td>
                  <td style={{ padding: '8px 10px' }}>
                    <div style={{ fontWeight: 600 }}>{row.actor_name || '—'}</div>
                    <div style={{ color: '#64748b' }}>{row.actor_email}</div>
                  </td>
                  <td style={{ padding: '8px 10px' }}>
                    <span style={{
                      padding: '2px 6px', borderRadius: 4, fontWeight: 700, fontSize: 11,
                      background: row.action === 'soft_delete' ? '#fee2e2' : row.action === 'restore' ? '#dcfce7' : '#e0f2fe',
                      color: row.action === 'soft_delete' ? '#991b1b' : row.action === 'restore' ? '#166534' : '#075985',
                    }}>
                      {row.action}
                    </span>
                  </td>
                  <td style={{ padding: '8px 10px' }}>
                    <div>{row.entity_type} {row.entity_id || ''}</div>
                    <div style={{ color: '#64748b' }}>{[row.discipline_code, row.job_id ? `job ${row.job_id}` : ''].filter(Boolean).join(' · ')}</div>
                  </td>
                  <td style={{ padding: '8px 10px', color: '#334155', maxWidth: 360 }}>
                    <div>{row.summary}</div>
                    {row.action === 'update' && (
                      <div style={{ color: '#64748b', marginTop: 4 }}>
                        {snapshotText(row.after_json) || snapshotText(row.before_json)}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '8px 10px' }}>
                    {row.action === 'soft_delete' && RESTORABLE.has(row.entity_type) && (
                      <button
                        type="button"
                        onClick={() => handleRestore(row)}
                        style={{
                          padding: '4px 8px', border: '1px solid #86efac', borderRadius: 6,
                          background: '#f0fdf4', color: '#166534', cursor: 'pointer', fontWeight: 700, fontSize: 11,
                        }}
                      >
                        Restore
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const filterStyle = {
  padding: '6px 8px',
  border: '1px solid #d1d5db',
  borderRadius: 6,
  fontSize: 12,
};

export default TimeTrackerAuditLog;
